import { useGenerationStatus } from '@hooks/apiHooks/generationsHooks';
import { useEffect, useMemo, useRef, useState } from 'react';

interface UseBusinessPlanParams {
  taskId?: string | null;
}

interface UseBusinessPlanResult {
  businessPlanUrl: string | null;
  isLoadingBusinessPlan: boolean;
  taskId: string | null;
}

export const useBusinessPlan = ({
  taskId,
}: UseBusinessPlanParams): UseBusinessPlanResult => {
  const { data: generationStatus, isLoading } = useGenerationStatus(
    taskId || null,
    !!taskId,
    false,
    false,
  );

  const prevStatusRef = useRef<string | undefined>(undefined);
  const [reloadTimestamp, setReloadTimestamp] = useState<number | null>(null);

  const baseBusinessPlanUrl = useMemo(() => {
    return generationStatus?.businessPlanUrl || null;
  }, [generationStatus?.businessPlanUrl]);

  useEffect(() => {
    const currentStatus = generationStatus?.status;
    if (
      prevStatusRef.current !== 'completed' &&
      currentStatus === 'completed' &&
      baseBusinessPlanUrl
    ) {
      setReloadTimestamp(Date.now());
    }
    prevStatusRef.current = currentStatus;
  }, [generationStatus?.status, baseBusinessPlanUrl]);

  const businessPlanUrl = useMemo(() => {
    if (!baseBusinessPlanUrl) return null;
    if (reloadTimestamp) {
      const separator = baseBusinessPlanUrl.includes('?') ? '&' : '?';
      return `${baseBusinessPlanUrl}${separator}_t=${reloadTimestamp}`;
    }
    return baseBusinessPlanUrl;
  }, [baseBusinessPlanUrl, reloadTimestamp]);

  const isLoadingBusinessPlan = useMemo(() => {
    if (!taskId) return false;
    if (isLoading) return true;
    if (!generationStatus) return true;
    if (generationStatus.status === 'completed' && baseBusinessPlanUrl)
      return false;
    if (generationStatus.status === 'failed') return false;
    return (
      generationStatus.status === 'processing' ||
      generationStatus.status === 'pending'
    );
  }, [taskId, isLoading, generationStatus, baseBusinessPlanUrl]);

  return {
    businessPlanUrl,
    isLoadingBusinessPlan,
    taskId: taskId || null,
  };
};
